<template>
  <Head title="Formasi" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Formasi', href: route('formasi.index') },
          ]"
        />

        <div
          class="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between"
        >
          <div>
            <h1 class="text-2xl font-bold sm:text-3xl text-slate-900">
              Formasi
            </h1>
          </div>
        </div>

        <!-- Main Grid Layout -->
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <!-- Form Section (Left) -->
          <div class="lg:col-span-1">
            <div
              class="p-6 bg-white border rounded-lg shadow-sm border-slate-200"
            >
              <h2 class="mb-6 text-lg font-semibold text-slate-900">
                {{ form.id ? "Edit Formasi" : "Tambah Formasi" }}
              </h2>

              <form @submit.prevent="submit" class="space-y-4">
                <!-- Dosen Selection -->
                <div>
                  <label
                    for="dosen_id"
                    class="block mb-2 text-sm font-medium text-slate-700"
                  >
                    Dosen
                  </label>
                  <div class="relative">
                    <!-- Search Input -->
                    <input
                      type="text"
                      v-model="dosenSearch"
                      @focus="showDosenDropdown = true"
                      @blur="hideDosenDropdown"
                      placeholder="Cari dosen..."
                      class="block w-full px-3 py-2 border rounded-md shadow-sm border-slate-300 focus:outline-none focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                      :class="{ 'border-red-300': form.errors.dosen_id }"
                    />

                    <!-- Dropdown -->
                    <div
                      v-if="showDosenDropdown && filteredDosen.length > 0"
                      class="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded-md shadow-lg border-slate-300 max-h-60"
                    >
                      <div
                        v-for="dsn in filteredDosen"
                        :key="dsn.id"
                        @click="selectDosen(dsn)"
                        class="px-3 py-2 text-sm border-b cursor-pointer hover:bg-slate-100 border-slate-100 last:border-b-0"
                      >
                        <div class="font-medium text-slate-900">
                          {{ dsn.nama }}
                        </div>
                        <div class="text-xs text-slate-500">
                          {{ dsn.fakultas?.name || "Fakultas tidak ditemukan" }}
                        </div>
                      </div>
                    </div>

                    <!-- No results -->
                    <div
                      v-if="
                        showDosenDropdown &&
                        filteredDosen.length === 0 &&
                        dosenSearch
                      "
                      class="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg border-slate-300"
                    >
                      <div class="px-3 py-2 text-sm text-slate-500">
                        Tidak ada dosen yang ditemukan
                      </div>
                    </div>

                    <!-- Clear button -->
                    <button
                      v-if="selectedDosen"
                      type="button"
                      @click="clearDosen"
                      class="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      <svg
                        class="w-4 h-4 text-slate-400 hover:text-slate-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"
                        ></path>
                      </svg>
                    </button>
                  </div>

                  <!-- Selected dosen display -->
                  <div
                    v-if="selectedDosen"
                    class="p-2 mt-2 border rounded-md bg-slate-50 border-slate-200"
                  >
                    <div class="text-sm font-medium text-slate-900">
                      {{ selectedDosen.nama }}
                    </div>
                    <div class="text-xs text-slate-500">
                      {{
                        selectedDosen.fakultas?.name ||
                        "Fakultas tidak ditemukan"
                      }}
                    </div>
                  </div>

                  <div
                    v-if="form.errors.dosen_id"
                    class="mt-1 text-sm text-red-600"
                  >
                    {{ form.errors.dosen_id }}
                  </div>
                </div>

                <!-- Jabatan Selection -->
                <div>
                  <label
                    for="jabatan_id"
                    class="block mb-2 text-sm font-medium text-slate-700"
                  >
                    Jabatan
                  </label>
                  <select
                    id="jabatan_id"
                    v-model="form.jabatan_id"
                    class="block w-full px-3 py-2 border rounded-md shadow-sm border-slate-300 focus:outline-none focus:ring-maroon-500 focus:border-maroon-500 sm:text-sm"
                    :class="{ 'border-red-300': form.errors.jabatan_id }"
                  >
                    <option value="">Pilih Jabatan</option>
                    <option
                      v-for="jabatan in props.jabatan?.data || []"
                      :key="jabatan.id"
                      :value="jabatan.id"
                    >
                      {{ jabatan.namajabatan }}
                    </option>
                  </select>
                  <div
                    v-if="form.errors.jabatan_id"
                    class="mt-1 text-sm text-red-600"
                  >
                    {{ form.errors.jabatan_id }}
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="flex flex-col gap-3 pt-4 sm:flex-row">
                  <button
                    type="submit"
                    :disabled="form.processing"
                    class="inline-flex items-center justify-center flex-1 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg
                      v-if="form.processing"
                      class="w-4 h-4 mr-2 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {{
                      form.processing
                        ? "Menyimpan..."
                        : form.id
                        ? "Update"
                        : "Simpan"
                    }}
                  </button>
                  <button
                    v-if="form.id"
                    type="button"
                    @click="resetForm"
                    class="inline-flex items-center justify-center flex-1 px-4 py-2 text-sm font-medium bg-white border rounded-md border-slate-300 text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
                  >
                    Batal
                  </button>
                </div>
              </form>
            </div>
          </div>
          <!-- Table Section (Right) -->
          <div class="lg:col-span-2">
            <!-- Search Input -->
            <div class="relative mb-4 text-sm text-slate-800">
              <div
                class="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none text-slate-400"
              >
                <MagnifyingGlassVue />
              </div>
              <input
                v-model="search"
                type="text"
                autocomplete="off"
                placeholder="Cari Nama Dosen..."
                id="search"
                class="block w-full py-2 pl-10 transition-colors duration-200 border-0 rounded-lg text-slate-900 ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-maroon-600 sm:text-sm sm:leading-6"
              />
            </div>

            <!-- Desktop Table -->
            <div class="hidden overflow-x-auto md:block">
              <table
                class="min-w-full bg-white border rounded-lg shadow-sm border-slate-200"
              >
                <thead class="bg-slate-50">
                  <tr>
                    <th
                      class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-6"
                    >
                      No
                    </th>
                    <th
                      class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-6"
                    >
                      Nama Dosen
                    </th>
                    <th
                      class="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                    >
                      Fakultas
                    </th>
                    <th
                      class="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                    >
                      Jabatan
                    </th>
                    <th class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span class="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-slate-200">
                  <tr
                    v-for="(formasi, index) in formasis?.data || []"
                    :key="formasi.id"
                    class="hover:bg-slate-50"
                  >
                    <td class="px-3 py-4 text-slate-500 whitespace-nowrap">
                      {{
                        ((formasis?.current_page || 1) - 1) *
                          (formasis?.per_page || 10) +
                        index +
                        1
                      }}
                    </td>
                    <td class="px-3 py-4 text-slate-700 whitespace-nowrap">
                      <div class="flex items-center space-x-3">
                        <div class="flex-shrink-0">
                          <img
                            v-if="formasi.dosen && formasi.dosen.foto"
                            :src="
                              formasi.dosen.foto.startsWith('http')
                                ? formasi.dosen.foto
                                : '/storage/' + formasi.dosen.foto
                            "
                            :alt="'Foto ' + formasi.dosen.nama"
                            class="object-cover w-10 h-10 border-2 rounded-full shadow-sm border-slate-200"
                            @error="$event.target.style.display = 'none'"
                          />
                          <div
                            v-else
                            class="flex items-center justify-center w-10 h-10 border-2 rounded-full bg-maroon-100 border-slate-200"
                          >
                            <svg
                              class="w-6 h-6 text-maroon-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                clip-rule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                        <div class="flex-1 min-w-0">
                          <span class="text-sm font-medium text-slate-900">{{
                            formasi.dosen.nama
                          }}</span>
                        </div>
                      </div>
                    </td>
                    <td class="px-3 py-4 text-slate-700 whitespace-nowrap">
                      {{ formasi.dosen.fakultas?.name || "-" }}
                    </td>
                    <td class="px-3 py-4 text-slate-700 whitespace-nowrap">
                      {{ formasi.jabatan.namajabatan }}
                    </td>
                    <td
                      class="relative py-4 pl-3 pr-4 text-right whitespace-nowrap sm:pr-6"
                    >
                      <div class="flex justify-end space-x-2">
                        <button
                          v-if="$page.props.can.formasi_edit"
                          @click="editFormasi(formasi)"
                          class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-maroon-700 bg-maroon-100 hover:bg-maroon-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
                        >
                          <svg
                            class="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                          Edit
                        </button>
                        <button
                          v-if="$page.props.can.formasi_delete"
                          @click="deleteFormasi(formasi)"
                          class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <svg
                            class="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Mobile Cards -->
            <div class="space-y-4 md:hidden">
              <div
                v-for="formasi in formasis?.data || []"
                :key="formasi.id"
                class="p-4 bg-white border rounded-lg shadow-sm border-slate-200"
              >
                <div class="flex items-start justify-between">
                  <div class="flex items-center space-x-3">
                    <div class="flex-shrink-0">
                      <img
                        v-if="formasi.dosen && formasi.dosen.foto"
                        :src="
                          formasi.dosen.foto.startsWith('http')
                            ? formasi.dosen.foto
                            : '/storage/' + formasi.dosen.foto
                        "
                        :alt="'Foto ' + formasi.dosen.nama"
                        class="object-cover w-12 h-12 border-2 rounded-full shadow-sm border-slate-200"
                        @error="$event.target.style.display = 'none'"
                      />
                      <div
                        v-else
                        class="flex items-center justify-center w-12 h-12 border-2 rounded-full bg-maroon-100 border-slate-200"
                      >
                        <svg
                          class="w-8 h-8 text-maroon-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-slate-900">
                        {{ formasi.dosen.nama }}
                      </p>
                      <p class="text-xs text-slate-500">
                        {{ formasi.dosen.fakultas?.name || "-" }}
                      </p>
                      <p class="text-xs text-slate-500">
                        {{ formasi.jabatan.namajabatan }}
                      </p>
                    </div>
                  </div>
                  <div class="flex space-x-2">
                    <button
                      v-if="$page.props.can.formasi_edit"
                      @click="editFormasi(formasi)"
                      class="p-2 border rounded-md text-maroon-600 border-maroon-200 hover:bg-maroon-50"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      v-if="$page.props.can.formasi_delete"
                      @click="deleteFormasi(formasi)"
                      class="p-2 text-red-600 border border-red-200 rounded-md hover:bg-red-50"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <Pagination
              :data="formasis"
              :updatedPageNumber="updatedPageNumber"
            />
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { useForm, usePage, router } from "@inertiajs/vue3";
import { ref, computed, watch, inject } from "vue";
import Swal from "sweetalert2";

import Pagination from "@/Components/Pagination.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Header from "@/Components/Header.vue";
import Container from "@/Layouts/Container.vue";
const route = inject('route');

const props = defineProps({
  formasis: {
    type: Object,
    required: true,
  },
  jabatan: {
    type: Object,
    required: true,
  },
  dosen: {
    type: Object,
    required: true,
  },
});

// Search and pagination
let search = ref(usePage().props.search || "");
let pageNumber = ref(1);

// Searchable dosen select
const dosenSearch = ref("");
const showDosenDropdown = ref(false);
const selectedDosen = ref(null);

// Computed property for filtered dosen
const filteredDosen = computed(() => {
  if (!dosenSearch.value) {
    return props.dosen?.data || [];
  }
  return (props.dosen?.data || []).filter((dsn) =>
    dsn.nama.toLowerCase().includes(dosenSearch.value.toLowerCase())
  );
});

// Methods for dosen selection
const selectDosen = (dsn) => {
  selectedDosen.value = dsn;
  form.dosen_id = dsn.id;
  dosenSearch.value = dsn.nama;
  showDosenDropdown.value = false;
};

const clearDosen = () => {
  selectedDosen.value = null;
  form.dosen_id = null;
  dosenSearch.value = "";
  showDosenDropdown.value = false;
};

const hideDosenDropdown = () => {
  // Add small delay to allow click events on dropdown items
  setTimeout(() => {
    showDosenDropdown.value = false;
  }, 150);
};

let formasiUrl = computed(() => {
  let url = new URL(route("formasi.index"));
  url.searchParams.set("page", pageNumber.value);
  if (search.value) {
    url.searchParams.set("search", search.value);
  }
  return url;
});

const updatedPageNumber = (link) => {
  const params = new URLSearchParams(link.url.split("?")[1]);
  pageNumber.value = params.get("page");
};

watch(
  () => formasiUrl.value,
  (updatedFormasiUrl) => {
    router.visit(updatedFormasiUrl.toString(), {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  }
);

watch(
  () => search.value,
  (value) => {
    if (value) {
      pageNumber.value = 1;
    }
  }
);

// Form handling
const form = useForm({
  id: null,
  dosen_id: null,
  jabatan_id: null,
});

const resetForm = () => {
  form.reset();
  form.clearErrors();
  // Reset dosen search
  selectedDosen.value = null;
  dosenSearch.value = "";
  showDosenDropdown.value = false;
};

const submit = () => {
  if (form.id) {
    // Update
    form.put(route("formasi.update", form.id), {
      onSuccess: () => {
        resetForm();
        Swal.fire({
          title: "Berhasil!",
          text: "Formasi berhasil diperbarui.",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#059669",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Gagal!",
          text: "Terjadi kesalahan saat memperbarui formasi.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#DC2626",
        });
      },
    });
  } else {
    // Create
    form.post(route("formasi.store"), {
      onSuccess: () => {
        resetForm();
        Swal.fire({
          title: "Berhasil!",
          text: "Formasi berhasil ditambahkan.",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#059669",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Gagal!",
          text: "Terjadi kesalahan saat menambahkan formasi.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#DC2626",
        });
      },
    });
  }
};

const editFormasi = (formasi) => {
  form.reset();
  form.clearErrors();
  form.id = formasi.id;
  form.dosen_id = formasi.iddosen;
  form.jabatan_id = formasi.idjabatan;

  // Set selected dosen for searchable select
  const dosen = props.dosen?.data?.find((d) => d.id === formasi.iddosen);
  if (dosen) {
    selectedDosen.value = dosen;
    dosenSearch.value = dosen.nama;
  }
};

const deleteFormasi = (formasi) => {
  Swal.fire({
    title: "Apakah Anda yakin?",
    text: `Formasi "${formasi.dosen.nama}" akan dihapus!`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#DC2626",
    cancelButtonColor: "#6B7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
    reverseButtons: true,
  }).then((result) => {
    if (result.isConfirmed) {
      router.delete(route("formasi.destroy", formasi.id), {
        onSuccess: () => {
          Swal.fire({
            title: "Terhapus!",
            text: "Formasi berhasil dihapus.",
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "#059669",
          });
        },
        onError: () => {
          Swal.fire({
            title: "Gagal!",
            text: "Terjadi kesalahan saat menghapus formasi.",
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonColor: "#DC2626",
          });
        },
      });
    }
  });
};
</script>
